import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EtatFiltrage from '../components/FiltreDepenses';
import { faCheckCircle, faTimesCircle, faInbox,faBalanceScale, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import pb from '../api/pocketbase';

// Déplacez cette fonction dans le composant ou passez setNotifications en paramètre
const supprimerNotification = async (notifId, setNotifications) => {
  try {
    await pb.collection('depensesRejeteesRT').delete(notifId);
    setNotifications((prev) => prev.filter((n) => n.id !== notifId));
  } catch (error) {
    console.error("Erreur lors de la suppression :", error);
  }
};

// Déclaration de la classe DepenseService au niveau supérieur
export class DepenseService {
  constructor() {
    this.pb = pb;
  }

  async validerDepense(depenseId) {
    try {
      // 1. D'abord, récupérer la dépense existante
      const depense = await this.pb.collection('depenses').getOne(depenseId);
      
      // 2. Préparer les données pour la validation
      const data = {
        nomProjet: depense.nomProjet,
        chefChantier: depense.chefChantier,
        rubriques: depense.rubriques,
        justificatif: depense.justificatif,
        status: "Validé",
        createdBy: depense.createdBy,
        dateValidationRT: new Date().toISOString(),
        montant: depense.montant,
        description: depense.description,
        fournisseur: depense.fournisseur
      };
      
      // 3. Créer l'enregistrement dans depensesValideesRT
      await this.pb.collection('depensesValideesRT').create(data);
      
      // 4. Mettre à jour le status de la dépense originale
      await this.pb.collection('depenses').update(depenseId, {
        status: "Validé",
        dateValidationRT: new Date().toISOString()
      });
    } catch (error) {
      console.error('Erreur détaillée:', error);
      throw error;
    }
  }

  async rejeterDepense(depenseId, motifRejet) {
    try {
      // 1. D'abord, récupérer la dépense existante
      const depense = await this.pb.collection('depenses').getOne(depenseId);
      
      // 2. Préparer les données pour le rejet
      const data = {
        nomProjet: depense.nomProjet,
        chefChantier: depense.chefChantier,
        rubriques: depense.rubriques,
        justificatif: depense.justificatif,
        status: "Rejeté",
        createdBy: depense.createdBy,
        dateValidationRT: new Date().toISOString(),
        montant: depense.montant,
        description: `${depense.description} - Motif du rejet: ${motifRejet}`,
        fournisseur: depense.fournisseur
      };
      
      // 3. Créer l'enregistrement dans depensesRejeteesRT
      await this.pb.collection('depensesRejeteesRT').create(data);
      
      // 4. Mettre à jour le status de la dépense originale
      await this.pb.collection('depenses').update(depenseId, {
        status: "Rejeté",
        dateValidationRT: new Date().toISOString(),
        description: `${depense.description} - Motif du rejet: ${motifRejet}`
      });
    } catch (error) {
      console.error('Erreur détaillée:', error);
      throw error;
    }
  }

  updateExpenseStatus(expenseId, status) {
    return this.pb.collection('depenses').update(expenseId, { status });
  }

  getExpensesEnAttente() {
    return this.pb.collection('depenses').getFullList({
      filter: "status = 'en_attente'"
    });
  }
}

export default function ResponsableTechniqueDashboard() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [demandesDepenses, setDemandesDepenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [depenseToReject, setDepenseToReject] = useState(null);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState("");
  const motifRef = useRef();

  // States for "Mettre en attente" modal
  const [showMEAmodal, setShowMEAmodal] = useState(false);
  const [depenseToMEA, setDepenseToMEA] = useState(null);
  const motifMEARef = useRef();

  const [filtreDateDebut, setFiltreDateDebut] = useState('');
  const [filtreDateFin, setFiltreDateFin] = useState('');
  const [depensesTraitees, setDepensesTraitees] = useState([]);
  const [historiqueValidees, setHistoriqueValidees] = useState([]);
  const [historiqueRejetees, setHistoriqueRejetees] = useState([]);
  const [loadingHistorique, setLoadingHistorique] = useState(false);
  const [notification, setNotification] = useState(null);
  const [rejectedNotifications, setRejectedNotifications] = useState([]);
  const [notifLoading] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [selectedValid, setSelectedValid] = useState(null);
  const [activeNotifTab, setActiveNotifTab] = useState("rejetées");
  const [validatedNotifications, setValidatedNotifications] = useState([]);
  const [depensesValideesEtat, setDepensesValideesEtat] = useState([]);
  const [depensesValideesDG, setDepensesValideesDG] = useState([]);
  const [activeView, setActiveView] = useState('dashboard');
  const [etatLoading, setEtatLoading] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  // Loading states pour prévenir les doubles clics
  const [validatingIds, setValidatingIds] = useState(new Set());
  const [rejectingIds, setRejectingIds] = useState(new Set());
  const [meaIds, setMeaIds] = useState(new Set());

  // Prevent back navigation after logout
  useEffect(() => {
    const handlePopState = (event) => {
      if (!pb.authStore.isValid) {
        event.preventDefault();
        window.history.pushState(null, null, '/login');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Enhanced logout handler
  const handleLogout = () => {
    // Clear authentication
    pb.authStore.clear();
    
    // Clear all local storage and session storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear any cached data
    window.location.replace('/login');
    
    // Prevent back navigation
    window.history.pushState(null, null, '/login');
    window.onpopstate = function () {
      window.history.pushState(null, null, '/login');
    };
  };

  // Ajoute ce useEffect pour charger les données de l'état DG quand activeView === 'etat'
useEffect(() => {
  if (activeView !== 'etat') return;
  setEtatLoading(true);
  pb.collection('depensesValideesDG')
    .getFullList({ sort: '-created' })
    .then(data => {
      setDepensesValideesEtat(data);
      setEtatLoading(false);
    })
    .catch(() => setEtatLoading(false));
}, [activeView]);

  // Ajoute ce useEffect pour charger les dépenses validées DG pour l'état
  useEffect(() => {
    if (activeTab !== "etat") return;
    const fetchEtatDG = async () => {
      try {
        const validesDG = await pb.collection('depensesValideesDG').getFullList({ sort: '-created' });
        setDepensesValideesDG(validesDG);
      } catch (err) {
        setMessage({ type: "error", text: "Erreur lors de la chargement de l'état DG." });
      }
    };
    fetchEtatDG();
  }, [activeTab]);

  const [showJustificatifModal, setShowJustificatifModal] = useState(false);
  const [justificatifUrl, setJustificatifUrl] = useState(null);

  // Ajout d'un state pour gérer les dépenses
  const [depenses, setDepenses] = useState([]);
  // Ajout des filtres manquants pour éviter l'erreur 'filtreDate is not defined'
  const [filtreDate, setFiltreDate] = useState('');
  // Ajout du filtreType pour éviter l'erreur 'filtreType is not defined'
  const [filtreType, setFiltreType] = useState('');

  const NotificationBox = () =>
    notification ? (
      <div
        className={`fixed top-20 right-8 z-50 px-6 py-4 rounded shadow-lg text-white font-semibold transition-all
          ${notification.type === "success" ? "bg-green-600" : "bg-red-600"}`}
      >
        <span className="mr-2">
          {notification.type === "success"
            ? "✓"
            : notification.type === "error"
            ? "✗"
            : "ℹ"}
        </span>
        {notification.text}
        <button
          className="ml-4 text-white font-bold"
          onClick={() => setNotification(null)}
        >
          ×
        </button>
      </div>
    ) : null;

  // Fonction pour valider une dépense et la stocker dans la collection depensesValideesRT
  const validerDepense = async (depense) => {
    setValidatingIds(prev => new Set(prev).add(depense.id));
    try {
      const formData = new FormData();

      // Récupération robuste du nom du projet
      const nomProjet =
        depense.nomProjet ||
        depense.projectNom ||
        depense.nom ||
        depense.projet?.nom ||
        depense.expand?.projet?.nom ||
        "Projet non spécifié";

      const donneesValidation = {
        nomProjet, // <-- Ajout ici
        chefChantier: depense.chefChantier,
        fournisseur: depense.fournisseur, 
        description: depense.description,
        montant: parseFloat(depense.montant),
        status: 'en_attente_CG',
        createdBy: pb.authStore.model?.id || 'inconnu',
        dateValidationRT: new Date().toISOString(),
        rubriques: typeof depense.rubriques === 'string' 
          ? depense.rubriques 
          : JSON.stringify(depense.rubriques)
      };

      for (const [key, value] of Object.entries(donneesValidation)) {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      }

      if (depense.justificatif) {
        const fileUrl = pb.files.getUrl(depense, depense.justificatif);
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const file = new File([blob], depense.justificatif, { type: blob.type });
        formData.append('justificatif', file);
      }

      await pb.collection('depensesValideesRT').create(formData);

      await pb.collection('demandesDepenses').update(depense.id, {
        status: 'Validé',
        dateValidationRT: new Date().toISOString()
      });

      if (typeof setNotification === 'function') {
        setNotification({ type: 'success', text: 'Dépense validée avec succès !' });
        setTimeout(() => setNotification(null), 1500);
      }

      setDemandesDepenses(prev => prev.filter(d => d.id !== depense.id));

    } catch (error) {
      console.error('Erreur détaillée:', error);
      if (typeof setNotification === 'function') {
        setNotification({ type: 'error', text: 'Erreur lors de la validation.' });
      }
      throw error;
    } finally {
      setValidatingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(depense.id);
        return newSet;
      });
    }
  };

  // Fonction pour rejeter une dépense et la stocker dans la collection depensesRejeteesRT
  const rejeterDepense = async (depense, motif) => {
    setRejectingIds(prev => new Set(prev).add(depense.id));
    try {
      const formData = new FormData();

      const nomProjet =
        depense.nomProjet ||
        depense.projectNom ||
        depense.nom ||
        depense.projet?.nom ||
        depense.expand?.projet?.nom ||
        "Projet non spécifié";

      const rejeterPar = pb.authStore.model?.name || pb.authStore.model?.username || pb.authStore.model?.email || "Responsable";

      const donneesRejet = {
        nomProjet, // <-- Ajout ici
        chefChantier: depense.chefChantier,
        fournisseur: depense.fournisseur,
        description: depense.description,
        montant: parseFloat(depense.montant),
        status: 'Rejeté',
        createdBy: pb.authStore.model.id,
        dateRejet: new Date().toISOString(),
        rubriques: typeof depense.rubriques === 'string' 
          ? depense.rubriques 
          : JSON.stringify(depense.rubriques),
        motifRejet: motif,
        rejeterPar: 'Responsable Technique',
        justificatif: depense.justificatif
      };

      for (const [key, value] of Object.entries(donneesRejet)) {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      }

      if (depense.justificatif) {
        const fileUrl = pb.files.getUrl(depense, depense.justificatif);
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const file = new File([blob], depense.justificatif, { type: blob.type });
        formData.set('justificatif', file);
      }

      await pb.collection('depensesRejeteesRT').create(formData);

      await pb.collection('demandesDepenses').update(depense.id, {
        status: 'Rejeté',
        dateRejet: new Date().toISOString(),
        motifRejet: motif,
        rejeterPar: rejeterPar,
        justificatif: depense.justificatif
      });

      if (typeof setNotification === 'function') {
        setNotification({ type: 'success', text: 'Dépense rejetée avec succès !' });
      }

      setDemandesDepenses(prev => prev.filter(d => d.id !== depense.id));

    } catch (error) {
      console.error('Erreur détaillée:', error);
      if (typeof setNotification === 'function') {
        setNotification({ type: 'error', text: 'Erreur lors du rejet.' });
      }
      throw error;
    } finally {
      setRejectingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(depense.id);
        return newSet;
      });
    }
  };

  const mettreEnAttenteDepense = async (depense, motif) => {
    setMeaIds(prev => new Set(prev).add(depense.id));
    try {
      const formData = new FormData();

      const nomProjet =
        depense.nomProjet ||
        depense.projectNom ||
        depense.nom ||
        depense.projet?.nom ||
        depense.expand?.projet?.nom ||
        "Projet non spécifié";

      const donneesMEA = {
        nomProjet,
        chefChantier: depense.chefChantier,
        fournisseur: depense.fournisseur,
        description: depense.description,
        montant: parseFloat(depense.montant),
        status: 'Mise en attente',
        createdBy: pb.authStore.model.id,
        datemea: new Date().toISOString(),
        rubriques: typeof depense.rubriques === 'string'
          ? depense.rubriques
          : JSON.stringify(depense.rubriques),
        motif: motif,
        meaPar:"Responsable Technique",
        justificatif: depense.justificatif
      };

      for (const [key, value] of Object.entries(donneesMEA)) {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      }

      if (depense.justificatif) {
        const fileUrl = pb.files.getUrl(depense, depense.justificatif);
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const file = new File([blob], depense.justificatif, { type: blob.type });
        formData.set('justificatif', file);
      }

      await pb.collection('depensemea').create(formData);

      await pb.collection('demandesDepenses').update(depense.id, {
        status: 'Mise en attente',
        datemea: new Date().toISOString(),
        motif: motif,
        meaPar: pb.authStore.model?.name || pb.authStore.model?.username || pb.authStore.model?.email || "Responsable Technique",
        justificatif: depense.justificatif
      });

      setNotification({ type: 'success', text: 'Dépense mise en attente avec succès !' });
      setTimeout(() => setNotification(null), 1500);
      setDemandesDepenses(prev => prev.filter(d => d.id !== depense.id));
    } catch (error) {
      console.error('Erreur détaillée:', error);
      setNotification({ type: 'error', text: 'Erreur lors de la mise en attente.' });
      throw error;
    } finally {
      setMeaIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(depense.id);
        return newSet;
      });
    }
  };

  const openMEAmodal = (depense) => {
    setDepenseToMEA(depense);
    setShowMEAmodal(true);
  };

  const confirmMEA = () => {
    const motif = motifMEARef.current.value.trim();
    if (!motif) {
      alert("Veuillez saisir un motif de mise en attente.");
      return;
    }
    setShowMEAmodal(false);
    mettreEnAttenteDepense(depenseToMEA, motif);
    setDepenseToMEA(null);
  };

  // Remplace la fonction filtrerDepenses par celle-ci pour que le filtre fonctionne sur la propriété générique "dateValidation"
  const filtrerDepenses = (depenses) => {
  if (!Array.isArray(depenses)) return [];

  return depenses.filter((depense) => {
    const dateValidation = depense.dateValidationRT
      ? (depense.dateValidationRT.seconds
          ? new Date(depense.dateValidationRT.seconds * 1000)
          : new Date(depense.dateValidationRT))
      : null;

    if (!dateValidation) return true;

    const dateDebut = filtreDateDebut ? new Date(filtreDateDebut) : null;
    const dateFin = filtreDateFin ? new Date(filtreDateFin + "T23:59:59") : null;

    return (!dateDebut || dateValidation >= dateDebut) &&
           (!dateFin || dateValidation <= dateFin);
  });
};

  // Fonction de filtrage des dépenses validées RT par date
  const filtrerDepensesValideesRT = (depenses) => {
    return depenses.filter((depense) => {
      const dateValidation = depense.dateValidationRT
        ? (depense.dateValidationRT.seconds
            ? new Date(depense.dateValidationRT.seconds * 1000)
            : new Date(depense.dateValidationRT))
        : null;
      if (!dateValidation) return true;
      const dateDebut = filtreDateDebut ? new Date(filtreDateDebut) : null;
      const dateFin = filtreDateFin ? new Date(filtreDateFin + "T23:59:59") : null;
      return (!dateDebut || dateValidation >= dateDebut) &&
             (!dateFin || dateValidation <= dateFin);
    });
  };

  // Fonction utilitaire pour regrouper les dépenses par rubrique et sous-rubrique
  function groupByRubriqueAndSousRubrique(depenses) {
    const result = {};
    depenses.forEach(depense => {
      let rubrique = "-";
      let sousRubrique = "-";
      if (Array.isArray(depense.rubriques) && depense.rubriques.length > 0) {
        rubrique = depense.rubriques[0].rubrique || depense.rubriques[0].nom || "-";
        sousRubrique = depense.rubriques[0].sousRubrique || "-";
      } else if (typeof depense.rubriques === "object" && depense.rubriques !== null) {
        rubrique = depense.rubriques.rubrique || depense.rubriques.nom || "-";
        sousRubrique = depense.rubriques.sousRubrique || "-";
      }
      if (!result[rubrique]) result[rubrique] = {};
      if (!result[rubrique][sousRubrique]) result[rubrique][sousRubrique] = [];
      result[rubrique][sousRubrique].push(depense);
    });
    return result;
  }

  // Fonction utilitaire pour regrouper les dépenses par projet
  function grouperDepensesParProjet(depenses) {
    return depenses.reduce((acc, depense) => {
      // On récupère le nom du projet de façon robuste
      const nomProjet =
        depense.nomProjet ||
        depense.projectNom ||
        depense.nom ||
        depense.projet?.nom ||
        depense.expand?.projet?.nom;
      
      // Ne garder que les dépenses avec un nom de projet valide (non vide et non "Projet non spécifié")
      if (nomProjet && nomProjet.trim() !== "" && nomProjet !== "Projet non spécifié") {
        if (!acc[nomProjet]) acc[nomProjet] = [];
        acc[nomProjet].push(depense);
      }
      return acc;
    }, {});
  }

  const organiserHistoriqueParProjet = (historique) => {
    return historique.reduce((acc, depense) => {
      const projetNom = depense.projet?.Nom || depense.projectNom || "Projet non spécifié";
      if (!acc[projetNom]) {
        acc[projetNom] = [];
      }
      acc[projetNom].push(depense);
      return acc;
    }, {});
  };

  // Correction : récupère rubrique/sousRubrique/fournisseur à la racine ou dans rubriques[0]
  const grouperDepensesParRubrique = (depenses) => {
    const result = {};
    depenses.forEach((depense) => {
      const rubrique = depense.rubrique || depense.rubriques?.[0]?.rubrique || "Rubrique non spécifiée";
      const sousRubrique = depense.sousRubrique || depense.rubriques?.[0]?.sousRubrique || "Sous-rubrique non spécifiée";
      if (!result[rubrique]) {
        result[rubrique] = {};
      }
      if (!result[rubrique][sousRubrique]) {
        result[rubrique][sousRubrique] = [];
      }
      result[rubrique][sousRubrique].push({
        ...depense,
        rubrique,
        sousRubrique,
        fournisseur: depense.fournisseur || depense.rubriques?.[0]?.fournisseur || "Non spécifié"
      });
    });
    return result;
  };

  const MessageNotification = () => {
    if (!message) return null;
    const bgColor =
      message.type === "success"
        ? "bg-green-100 text-green-800"
        : message.type === "error"
        ? "bg-red-100 text-red-800"
        : "bg-blue-100 text-blue-800";
    return (
      <div className={`p-4 ${bgColor} rounded-md mb-4 flex items-center shadow-sm`}>
        <span className="mr-2">
          {message.type === "success"
            ? "✓"
            : message.type === "error"
            ? "✗"
            : "ℹ"}
        </span>
        <span>{message.text}</span>
      </div>
    );
  };

  // NotificationsView pour Responsable Technique
    const NotificationsView = () => {
      const [waitingNotifications, setWaitingNotifications] = useState([]);
      const [validatedNotifications, setValidatedNotifications] = useState([]);
      const [notifLoading, setNotifLoading] = useState(true);
      const [selectedNotif, setSelectedNotif] = useState(null);
      const [activeNotifTab, setActiveNotifTab] = useState("en_attente");
      const [selectedNotifProject, setSelectedNotifProject] = useState(null);

      const [showMotifModal, setShowMotifModal] = useState(false);
      const [notifToMEA, setNotifToMEA] = useState(null);
      const motifNotifRef = useRef();

      // Ajout de l'état pour les notifications vues
      const [seenNotifIds, setSeenNotifIds] = useState(() =>
        JSON.parse(localStorage.getItem("seenNotifIdsRT") || "[]")
      );

      // Fonction pour marquer une notification comme vue
      const markAsSeen = (notifId) => {
        setSeenNotifIds((prev) => {
          if (prev.includes(notifId)) return prev;
          const newSeenIds = [...prev, notifId];
          localStorage.setItem("seenNotifIdsRT", JSON.stringify(newSeenIds));
          return newSeenIds;
        });
      };

      // ...dans NotificationsView, avant le return...

async function validerNotificationCG(notif) {
  try {
    // 1. Créer la dépense dans depensesValideesRT avec status 'en_attente_CG' pour que ComptableDashboard la prenne en charge
    const formData = new FormData();
    formData.append('nomProjet', notif.nomProjet || "");
    formData.append('chefChantier', notif.chefChantier || "");
    formData.append('fournisseur', notif.fournisseur || "");
    formData.append('description', notif.description || "");
    formData.append('montant', Number(notif.montant) || 0);
    formData.append('status', 'en_attente_CG');
    formData.append('createdBy', notif.createdBy || "");
    formData.append('dateValidationRT', new Date().toISOString());
    formData.append('rubriques', notif.rubriques
      ? (typeof notif.rubriques === 'string'
          ? notif.rubriques
          : JSON.stringify(notif.rubriques))
      : '');
    if (notif.justificatif) {
      try {
        const fileUrl = pb.files.getUrl(notif, notif.justificatif);
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const file = new File([blob], notif.justificatif, { type: blob.type });
        formData.append('justificatif', file);
      } catch (e) {}
    }
    await pb.collection('depensesValideesRT').create(formData);

    // 2. Mettre à jour la dépense dans depensemea (pour badge historique)
    await pb.collection('depensemea').update(notif.id, {
      status: 'ValidéRT',
      dateValidationRT: new Date().toISOString(),
      hasNewUpdate: true,
      updateCount: (notif.updateCount || 0) + 1,
      dateModification: new Date().toISOString()
    });

    setNotification({ type: 'success', text: 'Dépense validée et transférée au Responsable Comptable !' });
    setTimeout(() => setNotification(null), 1500);
   // setWaitingNotifications(prev => prev.filter(n => n.id !== notif.id));
  } catch (error) {
    setNotification({ type: 'error', text: 'Erreur lors de la validation.' });
    console.error(error);
  }
}

      // Pour masquer localement les notifications supprimées
      const [hiddenWaitingNotifIds, setHiddenWaitingNotifIds] = useState(() =>
        JSON.parse(localStorage.getItem("hiddenWaitingNotifIdsRT") || "[]")
      );
      const [hiddenValidatedNotifIds, setHiddenValidatedNotifIds] = useState(() =>
        JSON.parse(localStorage.getItem("hiddenValidatedNotifIdsRT") || "[]")
      );

      // Fonction pour masquer une notification localement
      const hideNotification = (notif, type) => {
        if (type === "en_attente") {
          const updated = [...hiddenWaitingNotifIds, notif.id];
          setHiddenWaitingNotifIds(updated);
          localStorage.setItem("hiddenWaitingNotifIdsRT", JSON.stringify(updated));
        } else if (type === "validées") {
          const updated = [...hiddenValidatedNotifIds, notif.id];
          setHiddenValidatedNotifIds(updated);
          localStorage.setItem("hiddenValidatedNotifIdsRT", JSON.stringify(updated));
        }
      };

      const mettreEnAttenteNotif = async (notif, motif) => {
        try {
          const formData = new FormData();
          const data = {
            nomProjet: notif.nomProjet,
            chefChantier: notif.chefChantier,
            fournisseur: notif.fournisseur,
            description: notif.description,
            montant: parseFloat(notif.montant),
            status: 'Mise en attente par RT',
            createdBy: notif.createdBy,
            datemea: new Date().toISOString(),
            rubriques: typeof notif.rubriques === 'string'
              ? notif.rubriques
              : JSON.stringify(notif.rubriques),
            motif: motif,
            meaPar: pb.authStore.model?.name || pb.authStore.model?.username || "Responsable Technique",
          };
      
          for (const [key, value] of Object.entries(data)) {
            if (value !== undefined && value !== null) {
              formData.append(key, value.toString());
            }
          }
      
          if (notif.justificatif) {
            const fileUrl = pb.files.getUrl(notif, notif.justificatif);
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const file = new File([blob], notif.justificatif, { type: blob.type });
            formData.append('justificatif', file);
          }
          
          await pb.collection('depensemea').create(formData);
          
          setNotification({ type: 'success', text: 'Dépense mise en attente avec succès !' });
          setTimeout(() => setNotification(null), 1500);
          hideNotification(notif, "en_attente");
      
        } catch (error) {
          console.error("Erreur lors de la mise en attente :", error);
          setNotification({ type: 'error', text: 'Erreur lors de la mise en attente.' });
        }
      };

      const confirmMettreEnAttente = () => {
        const motif = motifNotifRef.current.value.trim();
        if (!motif) {
          alert("Veuillez saisir un motif.");
          return;
        }
        if (notifToMEA) {
          mettreEnAttenteNotif(notifToMEA, motif);
        }
        setShowMotifModal(false);
        setNotifToMEA(null);
      };

      // Charger les notifications
      // ...dans NotificationsView...
      useEffect(() => {
        let unsubDepenseMea = null;
        let unsubValideesDG = null;

        const reloadNotifs = () => {
          setNotifLoading(true);
          Promise.all([
            pb.collection('depensemea').getFullList({
              filter: "meaPar = 'Controleur de Gestion' && (status = 'Mise en attente' || status = 'en_attente' || status = 'en_attente_CG')",
              sort: '-created'
            }),
            pb.collection('depensesValideesDG').getFullList({ sort: '-created' }),
          ]).then(([meaCG, valDG]) => {
            setWaitingNotifications(
              meaCG.filter(n => !hiddenWaitingNotifIds.includes(n.id))
                .sort((a, b) => new Date(b.created) - new Date(a.created))
            );
            setValidatedNotifications(
              valDG.filter(n => !hiddenValidatedNotifIds.includes(n.id))
                .sort((a, b) => new Date(b.created) - new Date(a.created))
            );
            setNotifLoading(false);
          }).catch(() => setNotifLoading(false));
        };

        reloadNotifs();

        pb.collection('depensemea').subscribe('*', reloadNotifs).then(unsub => { unsubDepenseMea = unsub; });
        pb.collection('depensesValideesDG').subscribe('*', reloadNotifs).then(unsub => { unsubValideesDG = unsub; });

        return () => {
          if (unsubDepenseMea) unsubDepenseMea();
          if (unsubValideesDG) unsubValideesDG();
        };
      }, [hiddenWaitingNotifIds, hiddenValidatedNotifIds]);

      // Utilitaire pour grouper par projet
      function getProjetNom(notif) {
        return (
          notif.nomProjet ||
          notif.projectNom ||
          notif.nom ||
          notif.projet?.nom ||
          notif.expand?.projet?.nom
        );
      }
      function groupByProjet(notifs) {
        return notifs.reduce((acc, notif) => {
          const projetNom = getProjetNom(notif);
          // Ne garder que les notifications avec un nom de projet valide (non vide et non "Projet non spécifié")
          if (projetNom && projetNom.trim() !== "" && projetNom !== "Projet non spécifié") {
            if (!acc[projetNom]) acc[projetNom] = [];
            acc[projetNom].push(notif);
          }
          return acc;
        }, {});
      }

      // Détails modale
      const renderNotifDetailsModal = (notif, type = "waiting") => {
        if (!notif) return null;
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 border-4 border-blue-200 animate-fade-in flex flex-col max-h-[90vh]">
              <div className="px-6 py-4 border-b border-blue-100 flex justify-between items-center rounded-t-2xl bg-gradient-to-r from-blue-50 to-blue-100">
                <h3 className="text-lg font-bold text-blue-800 flex items-center gap-2">
                  <span className={type === "waiting" ? "text-yellow-500" : "text-green-500"}>
                    {type === "waiting" ? "⏸️" : "✓"}
                  </span>
                  Détails de la dépense {type === "waiting" ? "mise en attente" : "validée"}
                </h3>
                <button
                  onClick={() => setSelectedNotif(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors text-2xl"
                >
                  ✕
                </button>
              </div>
              <div className="px-6 py-6 bg-gradient-to-br from-blue-50 to-white flex-1 overflow-y-auto">
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-blue-700">Rubrique :</span>
                    <span>
                      {notif.rubrique
                        || (Array.isArray(notif.rubriques) && notif.rubriques[0]?.rubrique)
                        || (typeof notif.rubriques === "object" && notif.rubriques?.rubrique)
                        || notif.rubriques?.nom
                        || "-"
                      }
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-blue-700">Sous-rubrique :</span>
                    <span>
                      {notif.sousRubrique
                        || (Array.isArray(notif.rubriques) && notif.rubriques[0]?.sousRubrique)
                        || (typeof notif.rubriques === "object" && notif.rubriques?.sousRubrique)
                        || "-"
                      }
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-blue-700">Montant :</span>
                    <span className="font-mono text-blue-900">{notif.montant?.toLocaleString()} FCFA</span>
                  </div>
                  <div>
                    <span className="font-semibold text-blue-700">Description :</span>
                    <div className="mt-1 bg-blue-50 rounded p-2 text-gray-700">{notif.description || "-"}</div>
                  </div>
                  {type === "waiting" && (
                    <>
                      <div>
                        <span className="font-semibold text-yellow-700">Motif de la mise en attente :</span>
                        <div className="mt-1 bg-yellow-50 rounded p-2 text-yellow-700">{notif.motif || "-"}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-blue-700">Date :</span>
                        <span>
                          {type === "waiting"
                            ? (notif.datemea
                                ? new Date(notif.datemea).toLocaleString()
                                : notif.created
                                  ? new Date(notif.created).toLocaleString()
                                  : "-")
                            : (notif.dateValidationDG
                                ? new Date(notif.dateValidationDG).toLocaleString()
                                : notif.created
                                  ? new Date(notif.created).toLocaleString()
                                  : "-")
                          }
                        </span>
                      </div>
                      {notif.fournisseur && (
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-blue-700">Fournisseur :</span>
                          <span>{notif.fournisseur}</span>
                        </div>
                      )}
                      <div>
                        <span className="font-semibold text-blue-700">Mise en attente par :</span>
                        <span className="font-bold">{notif.meaPar || "-"}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="px-6 py-4 bg-blue-50 flex justify-end rounded-b-2xl">
                <button
                  onClick={() => setSelectedNotif(null)}
                  className="px-5 py-2 bg-blue-200 text-blue-900 rounded-lg font-semibold hover:bg-blue-300 transition-colors shadow"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        );
      };

      return (
        <div className="flex-1 min-h-screen bg-gray-50 animate-fade-in p-0 m-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-blue-800 flex items-center gap-2">
                <span>🔔</span> Centre de notifications
              </h2>
              <p className="mt-2 text-gray-600">Consultez vos notifications de dépenses mises en attente et validées</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <button
                  onClick={() => setActiveNotifTab("en_attente")}
                  className={`flex-1 px-6 py-3 rounded-lg flex items-center justify-center gap-2 ${
                    activeNotifTab === "en_attente"
                      ? "bg-yellow-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <span className="text-lg">⏸️</span>
                  <span className="font-semibold">Dépenses en attente</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    activeNotifTab === "en_attente"
                      ? "bg-white text-yellow-600"
                      : "bg-yellow-200 text-yellow-700"
                  }`}>
                    {waitingNotifications.length}
                  </span>
                </button>
                <button
                  onClick={() => setActiveNotifTab("validées")}
                  className={`flex-1 px-6 py-3 rounded-lg flex items-center justify-center gap-2 ${
                    activeNotifTab === "validées"
                      ? "bg-green-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <span className="text-lg">✓</span>
                  <span className="font-semibold">Dépenses validées</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    activeNotifTab === "validées"
                      ? "bg-white text-green-600"
                      : "bg-green-200 text-green-700"
                  }`}>
                    {validatedNotifications.length}
                  </span>
                </button>
              </div>
              <div className="mt-6">
                {notifLoading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : activeNotifTab === "en_attente" ? (
                  waitingNotifications.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <span className="text-yellow-400 text-4xl mb-3">⏸️</span>
                      <p className="text-gray-600">Aucune dépense en attente</p>
                    </div>
                  ) : (
                    Object.entries(groupByProjet(waitingNotifications)).map(([projetNom, notifs]) => (
                      <div key={projetNom} className="mb-8">
                        <div
                          className={`cursor-pointer bg-gradient-to-br from-yellow-50 to-white rounded-xl shadow-lg p-5 border-l-8 border-yellow-500 mb-4 transition-transform hover:scale-105 hover:shadow-2xl ${
                            selectedNotifProject === projetNom ? "ring-2 ring-yellow-400" : ""
                          }`}
                          onClick={() => setSelectedNotifProject(selectedNotifProject === projetNom ? null : projetNom)}
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-lg text-yellow-900">{projetNom}</span>
                            <span className="ml-auto bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-bold">
                              {notifs.length} notification{notifs.length > 1 ? "s" : ""}
                            </span>
                          </div>
                        </div>
                        {selectedNotifProject === projetNom && (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {notifs.map((notif) => (
                              <div
                                key={notif.id}
                                className="relative bg-white rounded-lg shadow border-l-4 border-yellow-500 p-3 flex flex-col justify-between min-h-[140px] max-h-[180px] transition-transform hover:scale-[1.02] hover:shadow-lg"
                                style={{ fontSize: "0.93rem" }}
                              >
                                {!seenNotifIds.includes(notif.id) && (
                                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg">
                                    1
                                  </span>
                                )}
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-yellow-500 text-lg">⏸️</span>
                                  <span className="font-semibold text-xs text-yellow-700">Mise en attente</span>
                                </div>
                                <div className="text-[11px] text-gray-400 mb-1 italic">
                                  {notif.datemea
                                    ? new Date(notif.datemea).toLocaleString()
                                    : notif.created
                                      ? new Date(notif.created).toLocaleString()
                                      : "-"}
                                </div>
                                <div className="mb-2">
                                  <div className="font-bold text-xs text-yellow-700 truncate">{notif.rubrique}</div>
                                  <div className="text-xs text-gray-600 truncate">{notif.motif || "-"}</div>
                                  <div className="text-xs font-semibold text-gray-800 mt-1">
                                    {notif.montant?.toLocaleString()} FCFA
                                  </div>
                                </div>
                                <hr className="my-1 border-gray-200" />
                                <div className="flex justify-between items-center mt-1">
                                  <button
                                    className="text-blue-600 hover:underline text-xs font-semibold"
                                    onClick={() => {
                                      setSelectedNotif(notif);
                                      markAsSeen(notif.id);
                                    }}
                                  >
                                    Détails
                                  </button>
                                  <button
                                    className="text-green-600 hover:underline text-xs font-semibold"
                                    onClick={() => validerNotificationCG(notif)}
                                  >
                                    Valider
                                  </button>
                                  <button
                                    className="text-yellow-600 hover:underline text-xs font-semibold"
                                    onClick={() => {
                                      setNotifToMEA(notif);
                                      setShowMotifModal(true);
                                    }}
                                  >
                                    En attente
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )
                ) : (
                  validatedNotifications.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <span className="text-gray-400 text-4xl mb-3">✓</span>
                      <p className="text-gray-600">Aucune notification de validation</p>
                    </div>
                  ) : (
                    Object.entries(groupByProjet(validatedNotifications)).map(([projetNom, notifs]) => (
                      <div key={projetNom} className="mb-8">
                        <div
                          className={`cursor-pointer bg-gradient-to-br from-green-50 to-white rounded-xl shadow-lg p-5 border-l-8 border-green-500 mb-4 transition-transform hover:scale-105 hover:shadow-2xl ${
                            selectedNotifProject === projetNom ? "ring-2 ring-green-400" : ""
                          }`}
                          onClick={() => setSelectedNotifProject(selectedNotifProject === projetNom ? null : projetNom)}
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-lg text-green-900">{projetNom}</span>
                            <span className="ml-auto bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold">
                              {notifs.length} notification{notifs.length > 1 ? "s" : ""}
                            </span>
                          </div>
                        </div>
                        {selectedNotifProject === projetNom && (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {notifs.map((notif) => (
                              <div
                                key={notif.id}
                                className="relative bg-white rounded-lg shadow border-l-4 border-green-500 p-3 flex flex-col justify-between min-h-[140px] max-h-[180px] transition-transform hover:scale-[1.02] hover:shadow-lg"
                                style={{ fontSize: "0.93rem" }}
                              >
                                {!seenNotifIds.includes(notif.id) && (
                                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg">
                                    1
                                  </span>
                                )}
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-green-500 text-lg">✓</span>
                                  <span className="font-semibold text-xs text-green-700">Validé par DG</span>
                                </div>
                                <div className="text-[11px] text-gray-400 mb-1 italic">
                                  {notif.dateValidationDG
                                    ? new Date(notif.dateValidationDG).toLocaleString()
                                    : notif.created
                                      ? new Date(notif.created).toLocaleString()
                                      : "-"}
                                </div>
                                <div className="mb-2">
                                  <div className="font-bold text-xs text-green-700 truncate">{notif.rubrique}</div>
                                  <div className="text-xs text-gray-600 truncate">{notif.description || "-"}</div>
                                  <div className="text-xs font-semibold text-gray-800 mt-1">
                                    {notif.montant?.toLocaleString()} FCFA
                                  </div>
                                </div>
                                <hr className="my-1 border-gray-200" />
                                <div className="flex justify-between items-center mt-1">
                                  <button
                                    className="text-blue-600 hover:underline text-xs font-semibold"
                                    onClick={() => {
                                      setSelectedNotif(notif);
                                      markAsSeen(notif.id);
                                    }}
                                  >
                                    Détails
                                  </button>
                                  <button
                                    className="text-red-600 hover:underline text-xs font-semibold"
                                    onClick={() => hideNotification(notif, "validées")}
                                  >
                                    Supprimer
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )
                )}
              </div>
            </div>
          </div>
          {/* Modale détails */}
          {selectedNotif && renderNotifDetailsModal(
            selectedNotif,
            activeNotifTab === "en_attente" ? "waiting" : "validated"
          )}
          {showMotifModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Motif de la mise en attente</h3>
                  <button
                    onClick={() => setShowMotifModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                <div className="px-6 py-6">
                  <textarea
                    ref={motifNotifRef}
                    className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-yellow-200"
                    rows={4}
                    placeholder="Saisissez le motif de la mise en attente ici..."
                  />
                </div>
                <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2">
                  <button
                    onClick={() => setShowMotifModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={confirmMettreEnAttente}
                    className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                  >
                    Confirmer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    };

  // Fonction utilitaire pour obtenir l'URL du justificatif
  function getJustificatifUrl(depense) {
    if (!depense || !depense.justificatif) return null;
    return pb.files.getUrl(depense, depense.justificatif);
  }

  // Fonction de chargement des dépenses
  const chargerDepenses = async () => {
    try {
      const records = await pb.collection('depenses').getList(1, 50, {
        filter: "status = 'en_attente'"  // Ne charge que les dépenses en attente
      });
      setDepenses(records.items);
    } catch (error) {
      console.error("Erreur lors du chargement des dépenses:", error);
    }
  };

useEffect(() => {
  let unsubscribe = null;
  setLoading(true);

  // Fonction pour charger les dépenses en attente
  const chargerDemandesDepenses = async () => {
    try {
      // Première tentative : charger avec le filtre
      let data = await pb.collection('demandesDepenses').getFullList({
        filter: "status = 'en_attente'",
        sort: '-created'
      });
      
      console.log('✅ Dépenses avec status="en_attente":', data.length);
      
      // Si aucune dépense trouvée, charger TOUTES les dépenses pour diagnostiquer
      if (data.length === 0) {
        const toutesDepenses = await pb.collection('demandesDepenses').getFullList({
          sort: '-created'
        });
        console.warn('⚠️ Aucune dépense avec status="en_attente" trouvée.');
        console.warn('📊 Statuts disponibles dans demandesDepenses:', 
          [...new Set(toutesDepenses.map(d => d.status))]);
        console.warn('📋 Toutes les dépenses:', toutesDepenses.length);
      }
      
      setDemandesDepenses(data);
    } catch (error) {
      console.error('❌ Erreur lors du chargement automatique des données :', error);
      setDemandesDepenses([]);
    }
    setLoading(false);
  };

  // Chargement initial
  chargerDemandesDepenses();

  // Souscription en temps réel
  pb.collection('demandesDepenses').subscribe('*', function () {
    chargerDemandesDepenses();
  }).then((unsub) => {
    unsubscribe = unsub;
  });

  // Nettoyage à la destruction du composant
  return () => {
    if (unsubscribe) unsubscribe();
  };
}, []);

  // Ajoute ce useEffect pour charger l'historique depuis PocketBase
  useEffect(() => {
    if (activeTab !== "historique") return;
    setLoadingHistorique(true);

    const fetchHistorique = async () => {
      try {
        // Correction ici : on prend les validées dans depensesValideesDG
        const valides = await pb.collection('depensesValideesDG').getFullList({ sort: '-created' });
        setHistoriqueValidees(valides);

        // Les rejets restent dans depensesRejeteesRT
        const rejetees = await pb.collection('depensesRejeteesRT').getFullList({ sort: '-created' });
        setHistoriqueRejetees(rejetees);
      } catch (err) {
        setMessage({ type: "error", text: "Erreur lors du chargement de l'historique." });
      }
      setLoadingHistorique(false);
    };

    fetchHistorique();

    // Ecoute en temps réel sur les historiques
    const removeListenerValidees = pb.collection('depensesValideesDG').subscribe('*', fetchHistorique);
    const removeListenerRejetees = pb.collection('depensesRejeteesRT').subscribe('*', fetchHistorique);

    return () => {
      if (typeof removeListenerValidees === "function") removeListenerValidees();
      if (typeof removeListenerRejetees === "function") removeListenerRejetees();
    };
  }, [activeTab]);

  // Fonction pour mettre à jour le statut d'une dépense
  async function updateDepenseStatus(depenseId, newStatus) {
    try {
        await pb.collection('demandesDepenses').update(depenseId, {
            status: newStatus // 'validated' ou 'rejected'
        });
    } catch (error) {
        console.error('Erreur lors de la mise à jour:', error);
        throw error;
    }
  }

  // Fonction pour récupérer uniquement les dépenses en attente
  async function getDepensesEnAttente() {
    try {
        const depenses = await pb.collection('demandesDepenses').getList(1, 50, {
            filter: "status = 'en_attente'",
        });
        return depenses.items;
    } catch (error) {
        console.error('Erreur lors de la récupération:', error);
        throw error;
    }
  }

  async function handleValidation(depenseId) {
    try {
        await updateDepenseStatus(depenseId, 'validated');
        // Recharger uniquement les dépenses en attente
        depenses.value = await getDepensesEnAttente();
    } catch (error) {
        console.error(error);
    }
}

async function handleReject(depenseId) {
    try {
        await updateDepenseStatus(depenseId, 'rejected');
        // Recharger uniquement les dépenses en attente
        depenses.value = await getDepensesEnAttente();
    } catch (error) {
        console.error(error);
    }
}

  // Fonction utilitaire pour récupérer les dépenses par nom de projet
  async function getDepensesParNomProjet(nomProjet) {
    try {
      const depenses = await pb.collection('demandesDepenses').getFullList({
        filter: `nom = "${nomProjet}"`
      });
      return depenses;
    } catch (error) {
      console.error("Erreur lors de la récupération des dépenses par projet :", error);
      return [];
    }
  }

  // Exemple d'utilisation :
  useEffect(() => {
    async function chargerDepensesProjet() {
      const nomProjetRecherche = "Nom de ton projet"; // Remplace par la valeur recherchée
      const depensesProjet = await getDepensesParNomProjet(nomProjetRecherche);
      // Fais ce que tu veux avec depensesProjet
      console.log(depensesProjet);
    }
    // Appelle la fonction si besoin
    // chargerDepensesProjet();
  }, []);

  // Fonction de filtrage utilisée pour le DG
  const filteredDepenses = depenses.filter(depense => {
    const matchType = filtreType ? depense.type === filtreType : true;
    const matchDate = filtreDate ? depense.date === filtreDate : true;
    return matchType && matchDate;
  });

  // Pour le responsable technique, utilisez la même logique :
  const filteredDepensesRT = depenses.filter(depense => {
    const matchType = filtreType ? depense.type === filtreType : true;
    const matchDate = filtreDate ? depense.date === filtreDate : true;
    return matchType && matchDate;
  });

  // Ajoute cette fonction pour gérer le filtrage
  function handleFiltrerClick() {
    if (!filtreDateDebut && !filtreDateFin) {
      setMessage({ type: "error", text: "Veuillez saisir au moins une date de début ou de fin pour filtrer." });
      return;
    }
    // Ici tu lances le filtrage normalement (ex: setDepensesFiltrees(filtrerDepenses(...)))
  }


  return (
    <div className="bg-gray-50 text-gray-900 min-h-screen flex flex-col">
      <NotificationBox />
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 flex bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 h-14 items-center justify-between print:hidden">
        <div className="flex items-center space-x-4 min-w-0">
          <img src="/dywanes-logo.png" alt="Dywane" className="h-9 w-auto object-contain" style={{ maxHeight: "36px" }} />
        </div>
        <div className="flex-1 max-w-lg mx-4">
          <div className="relative text-gray-400 focus-within:text-gray-600">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">🔍</span>
            <input
              type="search"
              placeholder="Rechercher un projet..."
              className="block w-full rounded-md border border-gray-300 bg-gray-100 py-1.5 pl-10 pr-3 text-sm placeholder-gray-400 focus:border-gray-400 focus:ring-0 focus:outline-none"
            />
          </div>
        </div>
        <div className="flex items-center space-x-2 min-w-0">
          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-orange-100 text-orange-600 text-sm select-none">⚡</div>
          <span className="text-sm truncate min-w-0">Responsable Technique</span>
          <button className="text-gray-500 hover:text-gray-700 focus:outline-none">↩</button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <nav className="fixed top-14 left-0 z-40 h-[calc(100vh-3.5rem)] bg-gray-900 w-56 flex-shrink-0 px-4 py-6 flex flex-col space-y-6 text-gray-300 text-sm select-none print:hidden">
          <div>
            <button
              className={`flex items-center space-x-2 w-full rounded-md px-4 py-2 ${
                activeTab === "dashboard"
                  ? "bg-blue-800 text-white font-semibold"
                  : "hover:text-white"
              }`}
              onClick={() => setActiveTab("dashboard")}
            >
              <span>🏠</span>
              <span>Tableau de bord</span>
            </button>
          </div>
          <button
            className={`flex items-center space-x-2 w-full rounded-md px-4 py-2 ${
              activeTab === "etat"
                ? "bg-blue-800 text-white font-semibold"
                : "hover:text-white"
            }`}
            onClick={() => setActiveTab("etat")}
          >
            <span>📈</span>
            <span>État</span>
          </button>
          <button
            className={`flex items-center space-x-2 w-full rounded-md px-4 py-2 ${
              activeTab === "notifications"
                ? "bg-blue-800 text-white font-semibold"
                : "hover:text-white"
            }`}
            onClick={() => setActiveTab("notifications")}
          >
            <span>🔔</span>
            <span>Notifications</span>
          </button>

          <div className="flex-grow" />
          <div className="border-t border-gray-700 pt-6">
            <a href="#" className="flex items-center space-x-2 hover:text-white">
              <span>⚙</span>
              <span>Paramètres</span>
            </a>
          <button 
              className="flex items-center space-x-2 mt-4 hover:text-white w-full rounded-md px-4 py-2 text-left"
              onClick={() => setShowLogoutModal(true)}
            >
              <span>🚪</span>
              <span>Déconnexion</span>
            </button>
          </div>
        </nav>

        {/* Logout Confirmation Modal */}
        {showLogoutModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Confirmation de déconnexion</h3>
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <div className="px-6 py-6">
                <p className="text-gray-700 text-center">
                  Êtes-vous sûr de vouloir vous déconnecter ?
                </p>
              </div>
              <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Oui, se déconnecter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-auto p-6 pl-60 pt-20">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-lg font-bold text-gray-900">
              {activeTab === "dashboard"
                ? "Tableau de bord"
                : activeTab === "historique"
                ? "Historique des dépenses"
                : activeTab === "etat"
                ? "État"
                : ""}{" "}
              - Responsable Technique ⚡
            </h1>
          </div>

          <MessageNotification />

          {activeTab === "notifications" && <NotificationsView />}

          {activeTab === "dashboard" && (
            <div className="space-y-6">
              {loading ? (
                <div className="bg-white p-6 rounded-xl shadow border">
                  <div className="flex justify-center items-center h-40">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
                  </div>
                </div>
              ) : demandesDepenses.length === 0 ? (
                <div className="bg-white p-6 rounded-xl shadow border">
                  <div className="text-center text-gray-500">
                    <p className="text-lg">Aucune dépense en attente</p>
                  </div>
                </div>
              ) : (
                // Affichage groupé par projet puis par rubrique/sous-rubrique
                Object.entries(grouperDepensesParProjet(demandesDepenses)).map(([nomProjet, depensesProjet]) => {
                  const rubriques = groupByRubriqueAndSousRubrique(depensesProjet);
                  return (
                    <div key={nomProjet} className="bg-white rounded-xl shadow border mb-8">
                      <div className="px-6 py-4 border-b bg-blue-50 rounded-t-xl flex items-center">
                        <span className="font-bold text-blue-900 text-lg">{nomProjet}</span>
                        <span className="ml-4 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-bold">
                          {depensesProjet.length} dépense{depensesProjet.length > 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className="overflow-x-auto px-6 py-4">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead>
                            <tr>
                              <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rounded-tl-lg">
                                Rubrique
                              </th>
                              <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Sous Rubrique
                              </th>
                              <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Description
                              </th>
                              <th className="px-4 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Montant
                              </th>
                              <th className="px-4 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date demande
                              </th>
                              <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Fournisseur
                              </th>
                              <th className="px-4 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                              <th className="px-4 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Justificatif
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {Object.entries(rubriques).map(([rubrique, sousRubriques]) => (
                              <React.Fragment key={rubrique}>
                                <tr className="bg-blue-50">
                                  <td className="px-4 py-3 font-bold text-blue-900" colSpan={8}>
                                    {rubrique}
                                  </td>
                                </tr>
                                {Object.entries(sousRubriques).map(([sousRubrique, depensesList]) =>
                                  depensesList.map((depense, index) => (
                                    <tr key={depense.id} className="hover:bg-gray-50 transition-colors">
                                      <td className="px-4 py-3"></td>
                                      <td className="px-4 py-3 text-purple-700 font-semibold">{sousRubrique}</td>
                                      <td className="px-4 py-3 text-sm text-blue-700 underline cursor-pointer max-w-xs truncate text-center"
                                          onClick={() => {
                                            setSelectedDescription(depense.description || "-");
                                            setShowDescriptionModal(true);
                                          }}>
                                        {depense.description ? "Lire" : "-"}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                                        {Number(depense.montant).toLocaleString()} FCFA
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-500">
                                        {depense.date
                                          ? new Date(depense.date).toLocaleDateString('fr-FR', {
                                              day: '2-digit',
                                              month: 'short',
                                              year: 'numeric'
                                            })
                                          : "-"}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                        {depense.fournisseur}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                        <div className="flex items-center justify-center space-x-2">
                                          <button
                                            onClick={() => validerDepense(depense)}
                                            disabled={validatingIds.has(depense.id)}
                                            className={`inline-flex items-center justify-center p-2 ${
                                              validatingIds.has(depense.id)
                                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                                            } rounded-full transition-colors duration-200`}
                                            title={validatingIds.has(depense.id) ? 'Validation en cours...' : 'Valider cette dépense'}
                                          >
                                            {validatingIds.has(depense.id) ? (
                                              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.25"/>
                                                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" opacity="0.75"/>
                                              </svg>
                                            ) : (
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                              </svg>
                                            )}
                                          </button>
                                          <button
                                            onClick={() => openMEAmodal(depense)}
                                            disabled={meaIds.has(depense.id)}
                                            className={`inline-flex items-center justify-center p-2 ${
                                              meaIds.has(depense.id)
                                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                            } rounded-full transition-colors duration-200`}
                                            title={meaIds.has(depense.id) ? 'Traitement en cours...' : 'Mettre cette dépense en attente'}
                                          >
                                            {meaIds.has(depense.id) ? (
                                              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.25"/>
                                                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" opacity="0.75"/>
                                              </svg>
                                            ) : (
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l2 2" />
                                              </svg>
                                            )}
                                          </button>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                        {depense.justificatif ? (
                                          <button
                                            className="text-blue-700 underline"
                                            onClick={() => {
                                              setJustificatifUrl(getJustificatifUrl(depense));
                                              setShowJustificatifModal(true);
                                            }}
                                          >
                                            Voir
                                          </button>
                                        ) : (
                                          "-"
                                        )}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </React.Fragment>
                            ))}

                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}


          {activeTab === "etat" && (
            <div className="bg-white p-6 rounded-xl shadow border">
              <h2 className="text-xl font-bold mb-4">État des dépenses validées DG</h2>
              {/* Ajout du bouton Imprimer l'état */}
              <div className="flex justify-end mb-4 print:hidden">
                <button
                  onClick={() => window.print()}
                  className="px-4 py-2 bg-blue-700 text-white rounded shadow hover:bg-blue-800"
                >
                  🖨️ Imprimer l'état
                </button>
              </div>
              {etatLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600"></div>
                </div>
              ) : (
                <EtatFiltrage
                  historiqueValidees={depensesValideesEtat}
                  filtreDateDebut={filtreDateDebut}
                  setFiltreDateDebut={setFiltreDateDebut}
                  filtreDateFin={filtreDateFin}
                  setFiltreDateFin={setFiltreDateFin}
                />
              )}
            </div>
          )}

          {activeTab === "historique" && (
            <div className="space-y-10">
              {/* Dépenses validées */}
              <div className="bg-white rounded-2xl shadow-xl border border-green-200 mb-10">
                <div className="flex items-center px-8 py-5 border-b border-green-100 bg-green-50 rounded-t-2xl">
                  <span className="text-green-700 text-2xl mr-3">✓</span>
                  <h2 className="text-green-900 text-xl font-extrabold flex-1 tracking-wide">Projets avec dépenses validées</h2>
                  <span className="bg-green-100 text-green-800 px-4 py-1 rounded-full text-sm font-bold shadow">
                    {historiqueValidees.length} dépense{historiqueValidees.length > 1 ? "s" : ""}
                  </span>
                </div>
                {loadingHistorique ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-green-600"></div>
                  </div>
                ) : (
                  Object.entries(organiserHistoriqueParProjet(historiqueValidees)).map(([projetNom, depenses]) => {
                    // Grouper par rubrique et sous-rubrique
                    const rubriques = {};
                    depenses.forEach(dep => {
                      const rubrique = dep.rubrique || "Rubrique non spécifiée";
                      const sousRubrique = dep.sousRubrique || "Sous-rubrique non spécifiée";
                      if (!rubriques[rubrique]) rubriques[rubrique] = {};
                      if (!rubriques[rubrique][sousRubrique]) rubriques[rubrique][sousRubrique] = [];
                      rubriques[rubrique][sousRubrique].push(dep);
                    });

                    return (
                      <div key={projetNom} className="px-6 py-6">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="inline-block w-4 h-4 bg-green-600 rounded-full shadow"></span>
                          <span className="font-bold text-green-900 text-lg tracking-wide">{projetNom}</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full rounded-xl overflow-hidden shadow border border-green-100 bg-white">
                            <thead>
                              <tr>
                                <th className="px-4 py-3 bg-green-100 text-left text-xs font-bold text-green-800 uppercase">Rubrique</th>
                                <th className="px-4 py-3 bg-green-100 text-left text-xs font-bold text-green-800 uppercase">Sous-rubrique</th>
                                <th className="px-4 py-3 bg-green-100 text-left text-xs font-bold text-green-800 uppercase">Description</th>
                                <th className="px-4 py-3 bg-green-100 text-left text-xs font-bold text-green-800 uppercase">Chef de chantier</th>
                                <th className="px-4 py-3 bg-green-100 text-left text-xs font-bold text-green-800 uppercase">Fournisseur</th>
                                <th className="px-4 py-3 bg-green-100 text-right text-xs font-bold text-green-800 uppercase">Montant</th>
                                <th className="px-4 py-3 bg-green-100 text-center text-xs font-bold text-green-800 uppercase">Date validation</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(rubriques).map(([rubrique, sousRubriques], rIdx) => {
                                // Calcul du montant total de la rubrique
                                const montantTotalRubrique = Object.entries(sousRubriques).reduce((total, [_, depensesList]) => {
                                  return total + depensesList.reduce((sum, dep) => sum + Number(dep.montant || 0), 0);
                                }, 0);

                                // Récupérer le chef de chantier unique pour la rubrique
                                const chefChantier = Object.values(sousRubriques)
                                  .flat()
                                  .find(dep => dep.chefChantier)?.chefChantier || "Non spécifié";

                                return (
                                  <React.Fragment key={rubrique}>
                                    <tr className="bg-green-50">
                                      <td className="px-4 py-3 font-bold text-green-700 border-l-4 border-green-400">{rubrique}</td>
                                      <td></td>
                                      <td className="px-4 py-3 text-green-700">{chefChantier}</td>
                                      <td></td>
                                      <td className="px-4 py-3 text-right text-gray-900">
                                        {montantTotalRubrique.toLocaleString()} FCFA
                                      </td>
                                      <td></td>
                                    </tr>

                                    {Object.entries(sousRubriques).map(([sousRubrique, depensesList]) =>
                                      depensesList.map((depense, dIdx) => (
                                        <tr key={depense.id} className="bg-white hover:bg-gray-50/30">
                                          <td className="px-4 py-2"></td>
                                          <td className="px-4 py-2">{depense.fournisseur || "Non spécifié"}</td>
                                          <td className="px-4 py-2 text-right text-gray-900">
                                            {Number(depense.montant).toLocaleString()} FCFA
                                          </td>
                                          <td className="px-4 py-2 text-center">
                                            {/* Correction date validation */}
                                            {depense.dateValidationDG
                                              ? depense.dateValidationDG.seconds
                                                ? new Date(depense.dateValidationDG.seconds * 1000).toLocaleDateString('fr-FR')
                                                : new Date(depense.dateValidationDG).toLocaleDateString('fr-FR')
                                              : depense.dateValidationRT
                                                ? new Date(depense.dateValidationRT).toLocaleDateString('fr-FR')
                                                : depense.created
                                                  ? new Date(depense.created).toLocaleDateString('fr-FR')
                                                  : "-"}
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Dépenses rejetées */}
              <div className="bg-white rounded-2xl shadow-xl border border-red-200">
                <div className="flex items-center px-8 py-5 border-b border-red-100 bg-red-50 rounded-t-2xl">
                  <span className="text-red-700 text-2xl mr-3">✗</span>
                  <h2 className="text-red-900 text-xl font-extrabold flex-1 tracking-wide">Projets avec dépenses rejetées</h2>
                  <span className="bg-red-100 text-red-800 px-4 py-1 rounded-full text-sm font-bold shadow">
                    {historiqueRejetees.length} dépense{historiqueRejetees.length > 1 ? "s" : ""}
                  </span>
                </div>
                {loadingHistorique ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-red-600"></div>
                  </div>
                ) : (
                  Object.entries(organiserHistoriqueParProjet(historiqueRejetees)).map(([projetNom, depenses]) => {
                    // Grouper par rubrique et sous-rubrique
                    const rubriques = {};
                    depenses.forEach(dep => {
                      const rubrique = dep.rubrique || "Rubrique non spécifiée";
                      const sousRubrique = dep.sousRubrique || "Sous-rubrique non spécifiée";
                      if (!rubriques[rubrique]) rubriques[rubrique] = {};
                      if (!rubriques[rubrique][sousRubrique]) rubriques[rubrique][sousRubrique] = [];
                      rubriques[rubrique][sousRubrique].push(dep);
                    });

                    return (
                      <div key={projetNom} className="px-6 py-6">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="inline-block w-4 h-4 bg-red-600 rounded-full shadow"></span>
                          <span className="font-bold text-red-900 text-lg tracking-wide">{projetNom}</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full rounded-xl overflow-hidden shadow border border-red-100 bg-white">
                            <thead>
                              <tr>
                                <th className="px-4 py-3 bg-red-100 text-left text-xs font-bold text-red-800 uppercase">Rubrique</th>
                                <th className="px-4 py-3 bg-red-100 text-left text-xs font-bold text-red-800 uppercase">Sous-rubrique</th>
                                <th className="px-4 py-3 bg-red-100 text-left text-xs font-bold text-red-800 uppercase">Description</th>
                                <th className="px-4 py-3 bg-red-100 text-left text-xs font-bold text-red-800 uppercase">Chef de chantier</th>
                                <th className="px-4 py-3 bg-red-100 text-left text-xs font-bold text-red-800 uppercase">Fournisseur</th>
                                <th className="px-4 py-3 bg-red-100 text-right text-xs font-bold text-red-800 uppercase">Montant</th>
                                <th className="px-4 py-3 bg-red-100 text-center text-xs font-bold text-red-800 uppercase">Date rejet</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(rubriques).map(([rubrique, sousRubriques], rIdx) => {
                                // Calcul du montant total de la rubrique
                                const montantTotalRubrique = Object.entries(sousRubriques).reduce((total, [_, depensesList]) => {
                                  return total + depensesList.reduce((sum, dep) => sum + Number(dep.montant || 0), 0);
                                }, 0);

                                // Récupérer le chef de chantier unique pour la rubrique
                                const chefChantier = Object.values(sousRubriques)
                                  .flat()
                                  .find(dep => dep.chefChantier)?.chefChantier || "Non spécifié";

                                return (
                                  <React.Fragment key={rubrique}>
                                    <tr className="bg-red-50">
                                      <td className="px-4 py-3 font-bold text-red-700 border-l-4 border-red-400">{rubrique}</td>
                                      <td></td>
                                      <td className="px-4 py-3 text-red-700">{chefChantier}</td>
                                      <td></td>
                                      <td className="px-4 py-3 text-right text-gray-900">
                                        {montantTotalRubrique.toLocaleString()} FCFA
                                      </td>
                                      <td></td>
                                    </tr>

                                    {Object.entries(sousRubriques).map(([sousRubrique, depensesList]) =>
                                      depensesList.map((depense, dIdx) => (
                                        <tr key={depense.id} className="bg-white hover:bg-gray-50/30">
                                          <td className="px-4 py-2"></td>
                                          <td className="px-4 py-2 text-red-800 font-semibold">
                                            {/* Correction sous-rubrique */}
                                            {depense.sousRubrique
                                              || (Array.isArray(depense.rubriques) && depense.rubriques[0]?.sousRubrique)
                                              || (typeof depense.rubriques === "object" && depense.rubriques?.sousRubrique)
                                              || "Sous-rubrique non spécifiée"}
                                          </td>
                                          <td className="px-4 py-2 text-blue-700 underline cursor-pointer text-center"
                                              onClick={() => {
                                                setSelectedDescription(depense.description || "-");
                                                setShowDescriptionModal(true);
                                              }}>
                                            {depense.description ? "Lire" : "-"}
                                          </td>
                                          <td className="px-4 py-2"></td>
                                          <td className="px-4 py-2">{depense.fournisseur || "Non spécifié"}</td>
                                          <td className="px-4 py-2 text-right text-gray-900">
                                            {Number(depense.montant).toLocaleString()} FCFA
                                          </td>
                                          <td className="px-4 py-2 text-center">
                                            {/* Correction date validation */}
                                            {depense.dateValidationDG
                                              ? depense.dateValidationDG.seconds
                                                ? new Date(depense.dateValidationDG.seconds * 1000).toLocaleDateString('fr-FR')
                                                : new Date(depense.dateValidationDG).toLocaleDateString('fr-FR')
                                              : depense.dateValidationRT
                                                ? new Date(depense.dateValidationRT).toLocaleDateString('fr-FR')
                                                : depense.created
                                                  ? new Date(depense.created).toLocaleDateString('fr-FR')
                                                  : "-"}
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Ajout de la section État */}
          {activeView === 'etat' && (
            <div className="bg-white p-6 rounded-xl shadow border">
              <h2 className="text-xl font-bold mb-4">État des dépenses validées DG</h2>
              {/* Ajout du bouton Imprimer l'état */}
              <div className="flex justify-end mb-4 print:hidden">
                <button
                  onClick={() => window.print()}
                  className="px-4 py-2 bg-blue-700 text-white rounded shadow hover:bg-blue-800"
                >
                  🖨️ Imprimer l'état
                </button>
              </div>
              {etatLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600"></div>
                </div>
              ) : (
                <EtatFiltrage
                  historiqueValidees={depensesValideesEtat}
                  filtreDateDebut={filtreDateDebut}
                  setFiltreDateDebut={setFiltreDateDebut}
                  filtreDateFin={filtreDateFin}
                  setFiltreDateFin={setFiltreDateFin}
                />
              )}
            </div>
          )}

          {/* Modale de mise en attente */}
          {showMEAmodal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Motif de la mise en attente</h3>
                  <button
                    onClick={() => setShowMEAmodal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                <div className="px-6 py-6">
                  <textarea
                    ref={motifMEARef}
                    className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-yellow-200"
                    rows={4}
                    placeholder="Saisissez le motif de la mise en attente ici..."
                  />
                </div>
                <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2">
                  <button
                    onClick={() => setShowMEAmodal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={confirmMEA}
                    className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                  >
                    Mettre en attente
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modale de description et détails */}
          {showDescriptionModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              {selectedNotif ? (
                <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 border-4 border-yellow-200 animate-fade-in flex flex-col max-h-[90vh]">
                  <div className="px-6 py-4 border-b border-yellow-100 flex justify-between items-center rounded-t-2xl bg-gradient-to-r from-yellow-50 to-yellow-100">
                    <h3 className="text-lg font-bold text-yellow-800 flex items-center gap-2">
                      <span className="text-yellow-500">⏸️</span>
                      Détails de la dépense mise en attente
                    </h3>
                    <button
                      onClick={() => {
                        setShowDescriptionModal(false);
                        setSelectedNotif(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors text-2xl"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="px-6 py-6 bg-gradient-to-br from-yellow-50 to-white flex-1 overflow-y-auto">
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-yellow-700">Rubrique :</span>
                        <span>
                          {selectedNotif.rubrique
                            || (Array.isArray(selectedNotif.rubriques) && selectedNotif.rubriques[0]?.rubrique)
                            || (typeof selectedNotif.rubriques === "object" && selectedNotif.rubriques?.rubrique)
                            || selectedNotif.rubriques?.nom
                            || "-"
                          }
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-yellow-700">Sous-rubrique :</span>
                        <span>
                          {selectedNotif.sousRubrique
                            || (Array.isArray(selectedNotif.rubriques) && selectedNotif.rubriques[0]?.sousRubrique)
                            || (typeof selectedNotif.rubriques === "object" && selectedNotif.rubriques?.sousRubrique)
                            || "-"
                          }
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-yellow-700">Montant :</span>
                        <span className="font-mono text-yellow-900">{Number(selectedNotif.montant).toLocaleString()} FCFA</span>
                      </div>
                      <div>
                        <span className="font-semibold text-yellow-700">Description :</span>
                        <div className="mt-1 bg-yellow-50 rounded p-2 text-gray-700">{selectedNotif.description || "-"}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-yellow-700">Date :</span>
                        <span>
                          {selectedNotif.datemea
                            ? new Date(selectedNotif.datemea).toLocaleString()
                            : selectedNotif.created
                              ? new Date(selectedNotif.created).toLocaleString()
                              : "-"}
                        </span>
                      </div>
                      {selectedNotif.fournisseur && (
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-yellow-700">Fournisseur :</span>
                          <span>{selectedNotif.fournisseur}</span>
                        </div>
                      )}
                      <div>
                        <span className="font-semibold text-yellow-700">Mise en attente par :</span>
                        <span className="font-bold">{selectedNotif.meaPar || "-"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="px-6 py-4 bg-yellow-50 flex justify-end rounded-b-2xl">
                    <button
                      onClick={() => {
                        setShowDescriptionModal(false);
                        setSelectedNotif(null);
                      }}
                      className="px-5 py-2 bg-yellow-200 text-yellow-900 rounded-lg font-semibold hover:bg-yellow-300 transition-colors shadow"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Description</h3>
                    <button
                      onClick={() => setShowDescriptionModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="px-6 py-6">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedDescription}</p>
                  </div>
                  <div className="px-6 py-4 bg-gray-50 flex justify-end">
                    <button
                      onClick={() => setShowDescriptionModal(false)}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Modale de justificatif */}
          {showJustificatifModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Justificatif</h3>
                  <button
                 onClick={() => setShowJustificatifModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                <div className="px-6 py-6 overflow-auto max-h-[calc(90vh-120px)]">
                  {justificatifUrl && (
                    <iframe
                      src={justificatifUrl}
                      className="w-full h-96 border border-gray-300 rounded"
                      title="Justificatif"
                    />
                  )}
                </div>
                <div className="px-6 py-4 bg-gray-50 flex justify-end">
                  <button
                    onClick={() => setShowJustificatifModal(false)}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modale de déconnexion */}
          {showLogoutModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Confirmation de déconnexion</h3>
                  <button
                    onClick={() => setShowLogoutModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                <div className="px-6 py-6">
                  <p className="text-gray-700 text-center">
                    Êtes-vous sûr de vouloir vous déconnecter ?
                  </p>
                </div>
                <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2">
                  <button
                    onClick={() => setShowLogoutModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Oui, se déconnecter
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}